import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../../config/api';
import CertificateOffer from './CertificateOffer';
import '../../styles/Prova.css';

const approvalConfetti = Array.from({ length: 30 }, (_, index) => index);

const Prova = () => {
  const { id } = useParams();
  const [questions, setQuestions] = useState([]);
  const [cursoTitle, setCursoTitle] = useState('');
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [examResults, setExamResults] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(30 * 60); // 30 minutes
  const [timeExpired, setTimeExpired] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProva = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(buildApiUrl(`cursos/${id}/prova`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setCursoTitle(response.data.curso);
        setQuestions(response.data.questions);
        setLoading(false);
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          navigate('/login');
        } else {
          setError(error);
          setLoading(false);
        }
      }
    };

    fetchProva();
  }, [id, navigate]);

  // Timer effect
  useEffect(() => {
    if (timeRemaining > 0 && !showResults && !loading && !timeExpired) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && !showResults && !timeExpired) {
      setTimeExpired(true);
    }
  }, [timeRemaining, showResults, loading, timeExpired]);

  // Contagem regressiva de redirecionamento após tempo esgotado
  useEffect(() => {
    if (!timeExpired) return;
    if (redirectCountdown <= 0) {
      navigate(`/curso/${id}/estudo`);
      return;
    }
    const timer = setTimeout(() => {
      setRedirectCountdown(prev => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [timeExpired, redirectCountdown, id, navigate]);

  const handleOptionChange = (questionId, optionId) => {
    setResponses({
      ...responses,
      [questionId]: optionId,
    });
    setUnansweredQuestions(prevState => prevState.filter(id => id !== questionId));
  };

  const handleSubmit = async (e = null) => {
    if (e) e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);

    const unanswered = questions.filter(q => !responses[q.id]).map(q => q.id);

    if (unanswered.length > 0 && timeRemaining > 0) {
      setUnansweredQuestions(unanswered);
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
          buildApiUrl(`resposta-prova`),
        {
          curso_id: id,
          answers: Object.keys(responses).map(questionId => ({
            question_id: questionId,
            option_id: responses[questionId]
          }))
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const { message, score, passed, responses: serverResponses } = response.data;
      
      setExamResults({
        message,
        score,
        passed,
        responses: serverResponses,
        totalQuestions: questions.length,
        correctAnswers: Math.round((score / 100) * questions.length)
      });
      
      setShowResults(true);
      setTimeRemaining(0);
    } catch (error) {

      alert('Erro ao enviar a prova. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getProgress = () => {
    const answeredQuestions = Object.keys(responses).length;
    return Math.round((answeredQuestions / questions.length) * 100);
  };

  const canSubmit = () => {
    return Object.keys(responses).length === questions.length;
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  if (loading) {
    return (
      <div className="prova-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando prova...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prova-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Erro ao carregar prova</h2>
          <p>Não conseguimos carregar a prova deste curso. Tente novamente.</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="prova-page">
        <div className="error-container">
          <div className="error-icon">❓</div>
          <h2>Prova não disponível</h2>
          <p>Este curso ainda não possui uma prova configurada.</p>
          <Link to={`/curso/${id}/estudo`} className="back-link">
            Voltar ao Curso
          </Link>
        </div>
      </div>
    );
  }

  if (showResults && examResults) {
    const passed = examResults.passed;
    const formattedScore = Number(examResults.score).toLocaleString('pt-BR', {
      maximumFractionDigits: 2
    });

    if (passed) {
      return (
        <div className="prova-page approval-page">
          <main className="approval-shell">
            <div className="approval-confetti" aria-hidden="true">
              {approvalConfetti.map(piece => (
                <span key={piece} className={`confetti-piece confetti-piece-${piece + 1}`} />
              ))}
            </div>

            <section className="approval-hero" aria-labelledby="approval-title">
              <div className="approval-check" aria-hidden="true">
                <svg viewBox="0 0 64 64" role="img">
                  <path d="M18 32.5 27.5 42 47 21.5" />
                </svg>
              </div>

              <h1 id="approval-title">Parabéns, você foi aprovado!</h1>

              <div className="approval-score-pill" aria-label={`${formattedScore}% de aproveitamento`}>
                <span className="approval-score-star" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="m12 2.5 2.8 5.67 6.25.91-4.52 4.41 1.07 6.23L12 16.78l-5.6 2.94 1.07-6.23-4.52-4.41 6.25-.91L12 2.5Z" />
                  </svg>
                </span>
                <strong>{formattedScore}%</strong>
                <span>de aproveitamento</span>
              </div>

              <p className="approval-score-detail">
                Você acertou {examResults.correctAnswers} de {examResults.totalQuestions} questões
              </p>
            </section>

            <CertificateOffer courseId={id} courseName={cursoTitle} />

            <Link to="/meus-cursos" className="approval-back-link">
              Agora não, voltar aos meus cursos
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </Link>
          </main>
        </div>
      );
    }
    
    return (
      <div className="prova-page">
        <div className="results-container">
          <div className="results-card failed">
            <div className="results-icon">😔</div>
            
            <h2 className="results-title">Não foi desta vez...</h2>
            
            <div className="score-display">
              <div className="score-circle">
                <span>{formattedScore}%</span>
              </div>
              <p className="score-description">
                Você acertou {examResults.correctAnswers} de {examResults.totalQuestions} questões
              </p>
            </div>
            
            <div className="results-message">
              <div className="fail-message">
                <p>Você precisa de pelo menos 60% para ser aprovado. Estude mais e tente novamente!</p>
                <div className="retry-actions">
                  <Link to={`/curso/${id}/estudo`} className="study-button">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Estudar Mais
                  </Link>
                  <button
                    onClick={() => window.location.reload()}
                    className="retry-exam-button"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polyline points="23 4 23 10 17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M20.49 15C19.9828 16.8399 18.8927 18.4187 17.4015 19.5014C15.9103 20.5841 14.1204 21.1047 12.3061 20.9793C10.4917 20.8539 8.78254 20.0898 7.47998 18.8275C6.17742 17.5651 5.36898 15.8777 5.18501 14.0695C5.00104 12.2613 5.45649 10.4472 6.47998 8.94618C7.50348 7.44515 9.02716 6.34385 10.7934 5.83077C12.5596 5.31769 14.4537 5.42677 16.1491 6.14064C17.8445 6.8545 19.2474 8.13303 20.13 9.76" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Tentar Novamente
                  </button>
                </div>
              </div>
            </div>
            
            <div className="results-actions">
              <Link to="/meus-cursos" className="back-courses-button">
                Voltar aos Meus Cursos
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestionData = questions[currentQuestion];

  return (
    <div className="prova-page">
      {/* Modal: Tempo Esgotado */}
      {timeExpired && (
        <div className="time-expired-overlay">
          <div className="time-expired-modal">
            <div className="time-expired-icon">⏰</div>
            <h2 className="time-expired-title">Tempo Esgotado!</h2>
            <p className="time-expired-message">
              O tempo da prova acabou. Você será redirecionado para o estudo do curso em:
            </p>
            <div className="time-expired-countdown">{redirectCountdown}</div>
            <button
              className="time-expired-btn"
              onClick={() => navigate(`/curso/${id}/estudo`)}
            >
              Ir agora para o Estudo
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="exam-header">
        <div className="exam-info">
          <h1 className="exam-title">Prova - {cursoTitle}</h1>
          <p className="exam-subtitle">Responda todas as questões com atenção</p>
        </div>
        
        <div className="exam-stats">
          <div className="timer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className={timeRemaining < 300 ? 'time-warning' : ''}> 
              {formatTime(timeRemaining)}
            </span>
          </div>
          
          <div className="progress-info">
            <span className="progress-text">
              {Object.keys(responses).length} de {questions.length} respondidas
            </span>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${getProgress()}%` }}
              ></div>
            </div>
          </div>
        </div>
      </header>

      <div className="exam-layout">
        {/* Question Navigation Sidebar */}
        <aside className="question-nav">
          <h3 className="nav-title">Questões</h3>
          <div className="questions-grid">
            {questions.map((_, index) => (
              <button
                key={index}
                className={`question-number ${
                  index === currentQuestion ? 'current' : ''
                } ${
                  responses[questions[index].id] ? 'answered' : ''
                } ${
                  unansweredQuestions.includes(questions[index].id) ? 'unanswered' : ''
                }`}
                onClick={() => goToQuestion(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          <div className="nav-legend">
            <div className="legend-item">
              <div className="legend-color current"></div>
              <span>Atual</span>
            </div>
            <div className="legend-item">
              <div className="legend-color answered"></div>
              <span>Respondida</span>
            </div>
            <div className="legend-item">
              <div className="legend-color pending"></div>
              <span>Pendente</span>
            </div>
            <div className="legend-item">
              <div className="legend-color unanswered"></div>
              <span>Obrigatória</span>
            </div>
          </div>
        </aside>

        {/* Main Question Area */}
        <main className="question-area">
          <div className="question-card">
            <div className="question-header">
              <span className="question-counter">
                Questão {currentQuestion + 1} de {questions.length}
              </span>
              <span className={`question-status ${
                responses[currentQuestionData.id] ? 'answered' : 'pending'
              } ${
                unansweredQuestions.includes(currentQuestionData.id) ? 'unanswered' : ''
              }`}>
                {unansweredQuestions.includes(currentQuestionData.id) ? 'Obrigatória' :
                 responses[currentQuestionData.id] ? 'Respondida' : 'Pendente'}
              </span>
            </div>
            
            <div className="question-content">
              <h2 className="question-text">{currentQuestionData.question_text}</h2>
              
              <div className="answers-list">
                {currentQuestionData.options.map((option) => (
                  <label 
                    key={option.id} 
                    className={`answer-option ${
                      responses[currentQuestionData.id] === option.id ? 'selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question_${currentQuestionData.id}`}
                      value={option.id}
                      checked={responses[currentQuestionData.id] === option.id}
                      onChange={() => handleOptionChange(currentQuestionData.id, option.id)}
                    />
                    <div className="radio-custom"></div>
                    <span className="answer-text">{option.option_text}</span>
                  </label>
                ))}
              </div>
              
              {unansweredQuestions.includes(currentQuestionData.id) && (
                <div className="question-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Esta pergunta é obrigatória.
                </div>
              )}
            </div>
            
            <div className="question-navigation">
              <button
                onClick={previousQuestion}
                disabled={currentQuestion === 0}
                className="nav-btn prev"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Anterior
              </button>
              
              {currentQuestion === questions.length - 1 ? (
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit() || isSubmitting}
                  className="nav-btn next"
                >
                  {isSubmitting ? 'Enviando...' : 'Finalizar Prova'}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              ) : (
                <button
                  onClick={nextQuestion}
                  disabled={currentQuestion === questions.length - 1}
                  className="nav-btn next"
                >
                  Próxima
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Submit Section */}
      <footer className="submit-section">
        <div className="submit-card">
          <div className="submit-info">
            <h3>Finalizar Prova</h3>
            <p>
              Você respondeu {Object.keys(responses).length} de {questions.length} questões.
              {!canSubmit() && ' Complete todas as questões antes de enviar.'}
            </p>
          </div>
          
          <div className="submit-actions">
            <Link to={`/curso/${id}/estudo`} className="cancel-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Cancelar
            </Link>
            
            <button
              onClick={handleSubmit}
              disabled={!canSubmit() || isSubmitting}
              className="submit-button"
            >
              {isSubmitting ? (
                <>
                  <div className="submit-spinner"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4876 2.02168 11.3363C2.16356 9.18494 2.99721 7.13394 4.39828 5.49707C5.79935 3.86019 7.69279 2.72615 9.79619 2.24572C11.8996 1.76529 14.1003 1.95823 16.07 2.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Finalizar Prova
                </>
              )}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Prova;
